import { OTPInput as OTP } from "input-otp"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"

import { BaseInputHelpText } from "@molecules/Form/inputs/Input/InputHelpText/InputHelpText"

import useOTPForm from "@organisms/OTPForm/OTPForm.store"

import styles from "./OTPInput.module.scss"
import { OTPInputProps } from "./OTPInput.types"
import OTPInputSlot from "./OTPInputSlot/OTPInputSlot"

function OTPInput(props: OTPInputProps, ref: React.Ref<HTMLInputElement>) {
    const { isInvalid = false, isDisabled = false, onComplete, isTabbable, isFocused, inputHelpText, ...rest } = props

    const {
        handleCodeVerification,
        onOTPInputChange,
        otpInputValue,
        isCodeInvalid,
        errorMessage,
        isShowingErrorMessage,
    } = useOTPForm()

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleCodeVerification()
        }
    }

    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])

    useEffect(() => {
        if (isFocused && isTabbable) {
            inputRef.current?.focus()
        }
    }, [isFocused, isTabbable])

    const renderInputHelpText = () => {
        if (isDisabled || !isShowingErrorMessage) {
            return null
        }

        if (errorMessage) {
            return <BaseInputHelpText status="error" message={errorMessage.join(" ")} />
        }

        if (inputHelpText) {
            if (typeof inputHelpText === "string") {
                return <BaseInputHelpText status="neutral" message={inputHelpText} />
            } else {
                return <BaseInputHelpText {...inputHelpText} />
            }
        }

        return null
    }

    return (
        <div className={styles.wrapper}>
            <OTP
                ref={inputRef}
                name="otp"
                maxLength={6}
                containerClassName={styles.base}
                className={styles.input}
                disabled={isDisabled}
                value={otpInputValue}
                onChange={onOTPInputChange}
                onComplete={onComplete || handleCodeVerification}
                data-1p-ignore={true}
                data-lpignore={true}
                pushPasswordManagerStrategy="none"
                onKeyDown={onKeyDown}
                tabIndex={isTabbable ? 0 : -1}
                render={({ slots, isHovering }) => {
                    return (
                        <>
                            {slots.slice(0, 6).map((slot, idx) => (
                                <OTPInputSlot
                                    isInvalid={isInvalid || isCodeInvalid}
                                    key={idx}
                                    isDisabled={isDisabled}
                                    isHovering={isHovering}
                                    {...slot}
                                />
                            ))}
                        </>
                    )
                }}
                {...rest}
            />
            {renderInputHelpText()}
        </div>
    )
}

const OTPInputWithRef = forwardRef(OTPInput)
OTPInputWithRef.displayName = "Input"

export default OTPInputWithRef
